import React, { useState, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import SafeAnimatePresence from "../components/ui/SafeAnimatePresence";
import SignInPrompt from "../components/ui/SignInPrompt";
import SearchModal from "../components/ui/SearchModal";
import QuickActionsPalette from "../components/ui/QuickActionsPalette";
import HistoryModal from "../components/ui/HistoryModal";
import ArchiveChatButton from "../components/ui/ArchiveChatButton";
import {
  FiGrid,
  FiHash,
  FiBarChart2,
  FiRefreshCw,
  FiStar,
  FiSend,
  FiTrendingUp,
  FiPlus,
  FiMessageSquare,
  FiChevronLeft,
  FiLogOut,
  FiSettings,
  FiUser,
  FiHelpCircle,
  FiClock,
  FiEdit3,
  FiLock,
  FiZap,
  FiX,
  FiFileText,
  FiBookmark,
  FiCheck,
  FiAlertCircle,
  FiShoppingBag,
  FiCommand,
} from "react-icons/fi";
import { getDefaultPage, setDefaultPage, clearDefaultPage, getHiddenMenuItems, setHiddenMenuItems, getPinnedMenuItems, setPinnedMenuItems } from "../services/userPreferencesService";
import { fetchClawSkills, type ClawSkill } from "../services/momentumClawService";
import { isReviewQueueEnabled, isMarketplaceMenuEnabled } from "../utils/featureAccess";
import { NAV_BOTTOM_ITEMS } from "../constants/mockData";
import {
  CENTERS,
  PINNED_WORKSPACES,
  ALL_WORKSPACES,
  centerPath,
  findCenterForPath,
  isWorkspaceActive,
  isWorkspaceEnabled,
} from "../constants/centers";
import { recordCenterVisit } from "../features/quickActions/walkthroughProgress";
import { QUICK_ACTIONS_SHORTCUT_LABEL } from "../features/quickActions/platform";
import { useAuth } from "../features/auth/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { ROUTES } from "../app/routes";
import { useChannels } from "../context/ChannelContext";
import { useConversations } from "../context/ConversationContext";
import { useAmps } from "../context/AmpContext";
import { useInsuredDetail } from "../context/InsuredDetailContext";
import { usePolicyDetail } from "../context/PolicyDetailContext";

import type { NavItem, Amp, Channel } from "../features/chat/chat.types";
import logo from "../assets/images/iaai-logo-horizontal-color-dark-mode.svg";
import logoLight from "../assets/images/iaai-logo-horizontal-color-light-mode.svg";
import logoIcon from "../assets/images/M_Multicolor.svg";
import channelImg from "../assets/images/channel.png";
import marketplaceImg from "../assets/images/marketplace.png";
import searchImg from "../assets/images/search.png";
import SidebarDigitalTeam from "../components/ui/SidebarDigitalTeam";
import "../styles/SidebarLeft.css";

// Feather-style pushpin (same 2px stroke as the Fi icons around it); the
// slashed variant is "Unpin".
const IconPin = ({ off = false }: { off?: boolean }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 17v5" />
    <path d="M9 3h6l-1 6 4 4v2H6v-2l4-4-1-6z" />
    {off && <path d="M3 3l18 18" />}
  </svg>
);

const IconChevronUp = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 0 8 5" fill="none">
    <path
      d="M4 -6.51683e-07C4.11355 -6.56646e-07 4.21978 0.0230678 4.31868 0.0692037C4.41392 0.111495 4.50916 0.182622 4.6044 0.282583L7.79121 3.67935C7.9304 3.82545 8 4.00423 8 4.21569C8 4.43099 7.92857 4.61553 7.78571 4.76932C7.64286 4.92311 7.46886 5 7.26374 5C7.05861 5 6.87729 4.91542 6.71978 4.74625L3.99451 1.82814L1.28022 4.74625C1.12271 4.91542 0.941392 5 0.736264 5C0.531136 5 0.357143 4.92311 0.214286 4.76932C0.0714286 4.61553 -2.48723e-08 4.43099 -3.42834e-08 4.21569C-4.35265e-08 4.00423 0.069597 3.82545 0.208791 3.67935L3.3956 0.282583C3.57143 0.0941943 3.77289 -6.41756e-07 4 -6.51683e-07Z"
      fill="currentColor"
    />
  </svg>
);
const IconChevronDown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 0 8 5" fill="none">
    <path
      d="M4 5C3.88645 5 3.78022 4.97693 3.68132 4.9308C3.58608 4.8885 3.49084 4.81738 3.3956 4.71742L0.208791 1.32065C0.0695968 1.17455 1.18744e-08 0.995771 9.35285e-09 0.784314C6.7854e-09 0.569012 0.0714283 0.384468 0.214285 0.230681C0.357143 0.0768936 0.531136 8.90653e-08 0.736264 8.66192e-08C0.941392 8.41731e-08 1.12271 0.0845829 1.28022 0.253749L4.00549 3.17186L6.71978 0.253749C6.87729 0.0845829 7.05861 1.1226e-08 7.26374 8.77986e-09C7.46886 6.33373e-09 7.64286 0.0768935 7.78571 0.230681C7.92857 0.384468 8 0.569012 8 0.784314C8 0.995771 7.9304 1.17455 7.79121 1.32065L4.6044 4.71742C4.42857 4.90581 4.22711 5 4 5Z"
      fill="currentColor"
    />
  </svg>
);

const IconPlus = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      opacity="0.9"
      d="M6.75 0.75C6.75 0.335786 6.41421 0 6 0C5.58579 0 5.25 0.335786 5.25 0.75V5.25H0.75C0.335786 5.25 0 5.58579 0 6C0 6.41421 0.335786 6.75 0.75 6.75L5.25 6.75V11.25C5.25 11.6642 5.58579 12 6 12C6.41421 12 6.75 11.6642 6.75 11.25V6.75L11.25 6.75C11.6642 6.75 12 6.41421 12 6C12 5.58579 11.6642 5.25 11.25 5.25H6.75V0.75Z"
      fill="currentColor"
    />
  </svg>
);
const IconMinus = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="2" viewBox="0 0 12 2" fill="none">
    <path
      opacity="0.9"
      d="M6.59375 0.00114399C6.46484 0.00114399 6.41421 0.00114399 6 0.00114399C5.58579 0.00114399 5.5 0.00134903 5.36719 0L5.25 0.000741541H0.75C0.335786 0.000741541 0 0.336528 0 0.750741C0 1.16496 0.335786 1.50074 0.75 1.50074L5.25 1.50074L5.43359 1.50074C5.5625 1.50074 5.51333 1.50074 5.92755 1.50074C6.34176 1.50074 6.23047 1.50074 6.51953 1.50074L6.75 1.50074L11.25 1.50074C11.6642 1.50074 12 1.16496 12 0.750741C12 0.336529 11.6642 0.000742018 11.25 0.000742018H6.75L6.59375 0.00114399Z"
      fill="currentColor"
    />
  </svg>
);

interface SidebarLeftProps {
  isOpen: boolean;
  onClose: () => void;
  onAddChannel: () => void;
  onAddAmp: () => void;
  onAddClawSkill?: () => void;
  onEditClawSkill?: (skillId: string, skillName: string) => void;
  onClawNavClick?: () => void;
  onEditChannel?: (channel: Channel) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const ICON_MAP: Record<string, React.ReactNode> = {
  channels: <FiGrid size={16} />,
  campaigns: <FiBarChart2 size={16} />,
  renewals: <FiRefreshCw size={16} />,
  lead: <FiStar size={16} />,
  marketing: <FiSend size={16} />,
  performance: <FiTrendingUp size={16} />,
  add: <FiPlus size={16} />,
  messages: <FiMessageSquare size={16} />,
  marketplace: <img src={marketplaceImg} alt="" className="sidebar-icon sidebar-left__icon-marketplace" />,
  hash: <FiHash size={16} />,
  message: <FiMessageSquare size={16} />,
  trending: <FiTrendingUp size={16} />,
  refresh: <FiRefreshCw size={16} />,
  star: <FiStar size={16} />,
  chart: <FiBarChart2 size={16} />,
};

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString();
}

const getExpandedWidth = () => {
  if (window.innerWidth < 1280) return 210;
  if (window.innerWidth < 1440) return 228;
  return 256;
};

const SidebarLeft: React.FC<SidebarLeftProps> = ({ isOpen, onClose, onAddChannel, onAddAmp, onAddClawSkill, onEditClawSkill, onClawNavClick, onEditChannel, collapsed: collapsedProp, onToggleCollapse }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const { theme } = useTheme();
  const { threads, historyLoading } = useConversations();
  const { amps, premadeAmps } = useAmps();
  const { setSelectedInsuredId } = useInsuredDetail();
  const { setSelectedPolicyId } = usePolicyDetail();
  const [expandedWidth, setExpandedWidth] = useState(getExpandedWidth);

  useEffect(() => {
    const handler = () => setExpandedWidth(getExpandedWidth());
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  const { channels, hasChannels, deleteChannel, loading: channelsLoading } = useChannels();
  const navigate = useNavigate();
  const location = useLocation();
  // Rate SSO hand-off: sidebar shows only Submission Center's rating workspaces.
  const isRateOnly = localStorage.getItem('sourceTool') === 'Rate';

  // Opening a saved chat thread that was held with a Digital Workforce agent
  // re-assigns that same agent (so the next message continues via the
  // claude-managed /chat endpoint with its agent_id) — same as opening a
  // session from Digital Workforce's own chat history tab. Threads with no
  // agentId (the normal AMS copilot chats) navigate exactly as before.
  const openChatThread = useCallback((threadId: string) => {
    const thread = threads.find((t) => t.id === threadId);
    if (thread?.agentId) {
      navigate(`/chat/${threadId}`, {
        state: {
          forceHistoryRefresh: true,
          dwAgentId: thread.agentId,
          dwAgentName: thread.agentName || thread.agentId,
          dwSessionId: threadId,
        },
      });
    } else {
      navigate(`/chat/${threadId}`, {
        state: { forceHistoryRefresh: true },
      });
    }
  }, [threads, navigate]);

  const [activeId, setActiveId] = useState("renewals");
  const [chatActiveItem, setChatActiveItem] = useState<"new-chat" | "add-amp" | "add-channel" | null>(null);
  const [collapsedInternal, setCollapsedInternal] = useState(false);
  const collapsed = collapsedProp !== undefined ? collapsedProp : collapsedInternal;
  const toggleCollapse = useCallback(() => {
    if (onToggleCollapse) {
      onToggleCollapse();
    } else {
      setCollapsedInternal((p) => !p);
    }
  }, [onToggleCollapse]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [channelsExpanded, setChannelsExpanded] = useState(() => sessionStorage.getItem('sb-channels-expanded') === 'true');
  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(new Set());
  const [ampsExpanded, setAmpsExpanded] = useState(() => sessionStorage.getItem('sb-amps-expanded') === 'true');
  const [myToolsExpanded, setMyToolsExpanded] = useState(false);
  const [browserAgentSubExpanded, setBrowserAgentSubExpanded] = useState(true);
  const [applicationExpanded, setApplicationExpanded] = useState(
    () => location.pathname.startsWith('/claude-agents') || location.pathname.startsWith(ROUTES.MOMENTUM_CLAW)
  );
  const [clawSkills, setClawSkills] = useState<ClawSkill[]>([]);
  const isOnClawChat = location.pathname.length > ROUTES.MOMENTUM_CLAW.length && location.pathname.startsWith(ROUTES.MOMENTUM_CLAW + '/');

  useEffect(() => {
    if (!isOnClawChat) return;
    let cancelled = false;
    fetchClawSkills().then(skills => {
      if (!cancelled) setClawSkills(skills.filter(s => s.id && !s.id.startsWith('root@')));
    });
    return () => { cancelled = true; };
  }, [isOnClawChat]);
  const toggleApplication = useCallback(() => {
    if (collapsed) {
      setChatActiveItem(null);
      navigate(ROUTES.MOMENTUM_CLAW);
      onClawNavClick?.();
      onClose();
      return;
    }
    setApplicationExpanded((p) => !p);
  }, [collapsed, navigate, onClawNavClick, onClose]);
  const [defaultPagePath, setDefaultPagePath] = useState<string | null>(null);
  const [defaultSavingPath, setDefaultSavingPath] = useState<string | null>(null);
  const [defaultToast, setDefaultToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [hiddenMenuItems, setHiddenMenuItemsState] = useState<Set<string>>(() => {
    try {
      const cached = localStorage.getItem('ig-hidden-menu-items');
      return cached !== null ? new Set(cached.split(',').filter(Boolean)) : new Set();
    } catch { return new Set(); }
  });
  const [hiddenMenuItemsLoaded, setHiddenMenuItemsLoaded] = useState(() => {
    try { return localStorage.getItem('ig-hidden-menu-items') !== null; } catch { return false; }
  });
  const [showHiddenTools, setShowHiddenTools] = useState(false);
  // Centers are accordion-exclusive — opening one closes any other, so the
  // Workspaces tree never grows past "1 pinned group + 1 open Center" (was:
  // all 5 Centers could stay open at once, exposing all 15 workspaces
  // simultaneously — the #1 finding of the 2026-09-23 nav critique). The
  // Center you're currently working in still opens itself on load/deep-link,
  // so navigating never leaves you stranded.
  const [expandedCenterId, setExpandedCenterId] = useState<string | null>(() => {
    try {
      const saved = sessionStorage.getItem('sb-expanded-center');
      if (saved) return saved;
    } catch {
      /* fall through to default */
    }
    const current = findCenterForPath(window.location.pathname);
    return current ? current.id : null;
  });
  const [menuItemSaving, setMenuItemSaving] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isAuthenticated) return;
    getDefaultPage().then((p) => setDefaultPagePath(p));
    getHiddenMenuItems().then((items) => {
      setHiddenMenuItemsState(new Set(items));
      setHiddenMenuItemsLoaded(true);
      try { localStorage.setItem('ig-hidden-menu-items', items.join(',')); } catch {}
    });
  }, [isAuthenticated]);

  const handleSetDefault = useCallback(async (path: string, name: string) => {
    setDefaultSavingPath(path);
    const ok = await setDefaultPage(path, name);
    setDefaultSavingPath(null);
    if (ok) {
      setDefaultPagePath(path);
      setDefaultToast({ type: 'success', message: `"${name}" set as default page` });
    } else {
      setDefaultToast({ type: 'error', message: 'Failed to set default page' });
    }
    setTimeout(() => setDefaultToast(null), 3500);
  }, []);

  const handleRemoveDefault = useCallback(async () => {
    const ok = await clearDefaultPage();
    if (ok) {
      setDefaultPagePath(null);
      setDefaultToast({ type: 'success', message: 'Default page removed' });
    } else {
      setDefaultToast({ type: 'error', message: 'Failed to remove default page' });
    }
    setTimeout(() => setDefaultToast(null), 3500);
  }, []);

  const handleHideMenuItem = useCallback(async (path: string) => {
    setMenuItemSaving(prev => { const s = new Set(prev); s.add(path); return s; });
    const next = new Set(hiddenMenuItems);
    next.add(path);
    try { localStorage.setItem('ig-hidden-menu-items', Array.from(next).join(',')); } catch {}
    await setHiddenMenuItems(Array.from(next));
    setHiddenMenuItemsState(next);
    setMenuItemSaving(prev => { const s = new Set(prev); s.delete(path); return s; });
  }, [hiddenMenuItems]);

  const handleShowMenuItem = useCallback(async (path: string) => {
    setMenuItemSaving(prev => { const s = new Set(prev); s.add(path); return s; });
    const next = new Set(hiddenMenuItems);
    next.delete(path);
    try { localStorage.setItem('ig-hidden-menu-items', Array.from(next).join(',')); } catch {}
    await setHiddenMenuItems(Array.from(next));
    setHiddenMenuItemsState(next);
    if (next.size === 0) setShowHiddenTools(false);
    setMenuItemSaving(prev => { const s = new Set(prev); s.delete(path); return s; });
  }, [hiddenMenuItems]);

  // Workspaces pinned (from their "⋯" menu) as icon-only shortcuts above the
  // Workspaces tree, in the order they were pinned. Saved per user in user
  // preferences (like hidden items and the default page); localStorage is
  // only a cache so the pins show before that loads.
  const [pinnedPaths, setPinnedPaths] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('ig-pinned-sidebar-items');
      return raw ? raw.split(',').filter(Boolean) : [];
    } catch { return []; }
  });
  useEffect(() => {
    if (!isAuthenticated) return;
    getPinnedMenuItems().then((items) => {
      if (!items) return;
      setPinnedPaths(items);
      try { localStorage.setItem('ig-pinned-sidebar-items', items.join(',')); } catch {}
    });
  }, [isAuthenticated]);
  const savePinned = useCallback((next: string[]) => {
    setPinnedPaths(next);
    try { localStorage.setItem('ig-pinned-sidebar-items', next.join(',')); } catch {}
    void setPinnedMenuItems(next);
  }, []);
  const togglePinned = useCallback((path: string) => {
    savePinned(pinnedPaths.includes(path) ? pinnedPaths.filter(p => p !== path) : [...pinnedPaths, path]);
  }, [pinnedPaths, savePinned]);
  // Drag (or Alt+Arrow) to rearrange pinned icons; the order is the order
  // they render in.
  const movePinned = useCallback((path: string, toIndex: number) => {
    const from = pinnedPaths.indexOf(path);
    if (from === -1) return;
    const next = pinnedPaths.filter(p => p !== path);
    next.splice(Math.max(0, Math.min(toIndex, next.length)), 0, path);
    if (next.join(',') === pinnedPaths.join(',')) return;
    savePinned(next);
  }, [pinnedPaths, savePinned]);
  const [draggingPin, setDraggingPin] = useState<string | null>(null);
  const [pinDropIndex, setPinDropIndex] = useState<number | null>(null);
  const pinnedWorkspaces = pinnedPaths
    .map(path => ALL_WORKSPACES.find(w => w.path === path))
    .filter((w): w is (typeof ALL_WORKSPACES)[number] => !!w && isWorkspaceEnabled(w));

  const toggleMyTools = useCallback(() => setMyToolsExpanded((p) => !p), []);

  const [signInPrompt, setSignInPrompt] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [quickActionsViaKeyboard, setQuickActionsViaKeyboard] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [channelMenuOpen, setChannelMenuOpen] = useState<string | null>(null);
  const [navMenuOpen, setNavMenuOpen] = useState<{ path: string; name: string } | null>(null);
  const [deleteConfirmChannel, setDeleteConfirmChannel] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right?: number; left?: number } | null>(null);
  const [uploadChannelGuid, setUploadChannelGuid] = useState<string | null>(null);
  const [channelUploading, setChannelUploading] = useState(false);
  const [, setChannelUploadedDocs] = useState<{ fileUrl: string; fileName: string }[]>([]);
  const [channelViewDoc, setChannelViewDoc] = useState<{ fileUrl: string; fileName: string } | null>(null);
  const [channelSelectedFile, setChannelSelectedFile] = useState<File | null>(null);
  const [channelUploadError, setChannelUploadError] = useState<string | null>(null);
  const [channelIsDragOver, setChannelIsDragOver] = useState(false);
  const [channelModalUploads, setChannelModalUploads] = useState<{ fileUrl: string; fileName: string }[]>([]);
  const channelFileInputRef = useRef<HTMLInputElement>(null);
  const [mcpPopoverChannel, setMcpPopoverChannel] = useState<string | null>(null);
  const [mcpPopoverPos, setMcpPopoverPos] = useState<{ top: number; left: number } | null>(null);

  const toggleCenter = useCallback((centerId: string) => {
    setExpandedCenterId(prev => {
      const next = prev === centerId ? null : centerId;
      try {
        if (next) sessionStorage.setItem('sb-expanded-center', next);
        else sessionStorage.removeItem('sb-expanded-center');
      } catch {
        /* session storage is a convenience, not a requirement */
      }
      return next;
    });
  }, []);

  // Opening a workspace directly (deep link, search, "..." menu) reveals its Center.
  useEffect(() => {
    const current = findCenterForPath(location.pathname);
    if (!current) return;
    setExpandedCenterId(current.id);
    recordCenterVisit(current.id);
  }, [location.pathname]);

  // Global ⌘K / Ctrl+K — opens Quick Actions from anywhere, same convention
  // Raycast/Linear/Slack use for their command palettes.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(false);
        setQuickActionsViaKeyboard(true);
        setQuickActionsOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const openWorkspaceMenu = useCallback((e: React.MouseEvent | React.KeyboardEvent, path: string, name: string) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // Right-aligned to the trigger, unless that would run off the left edge
    // (pinned icons sit at the far left) — then open rightwards from it.
    const MENU_WIDTH = 220;
    setMenuPosition(rect.right - MENU_WIDTH < 8
      ? { top: rect.bottom + 4, left: Math.max(8, rect.left) }
      : { top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setNavMenuOpen(navMenuOpen?.path === path ? null : { path, name });
    setChannelMenuOpen(null);
  }, [navMenuOpen]);

  const handleNewChat = useCallback(() => {
    setChatActiveItem('new-chat');
    setSelectedInsuredId(null);
    setSelectedPolicyId(null);
    const channelMatch = location.pathname.match(/^\/channel\/([^/]+)/);
    const ampMatch = location.pathname.match(/^\/amp\/([^/]+)/);
    const clawMatch = location.pathname.match(/^\/momentum-claw/);
    const agentPageMatch = location.pathname.match(/^\/claude-agents/);
    const chatMatch = location.pathname.startsWith('/chat');
    if (agentPageMatch) {
      // On sub-pages (skills / agent-config / mcp-config) navigate to chat root;
      // on the chat page itself dispatch the in-page "new chat" event.
      const isSubPage = /^\/claude-agents\/(skills|agent-config|mcp-config)/.test(location.pathname);
      if (isSubPage) {
        navigate(ROUTES.CLAUDE_AGENTS);
      } else {
        window.dispatchEvent(new CustomEvent('newAgentChat'));
      }
    } else if (channelMatch) {
      navigate(`/channel/${channelMatch[1]}${location.search}`);
    } else if (ampMatch) {
      navigate(`/amp/${ampMatch[1]}`);
    } else if (clawMatch) {
      const newId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
      navigate(`/momentum-claw/${newId}`);
    } else if (chatMatch) {
      window.dispatchEvent(new CustomEvent('newChatConversation'));
    } else {
      navigate(ROUTES.CHAT);
    }
    onClose();
  }, [location.pathname, location.search, navigate, onClose, setSelectedInsuredId, setSelectedPolicyId]);

  const handleShowHiddenWorkspaces = useCallback(() => {
    if (collapsed) toggleCollapse();
    setChannelsExpanded(true);
    try { sessionStorage.setItem('sb-channels-expanded', 'true'); } catch { /* convenience only */ }
    setShowHiddenTools(true);
  }, [collapsed, toggleCollapse]);

  const activateOnKey = (action: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  const toggleChannels = useCallback(() => {
    if (!isAuthenticated) {
      setSignInPrompt("Sign in to create channels");
      return;
    }
    if (collapsed) {
      if (channels.length > 0) {
        navigate(`/channel/${channels[0].guid}`);
        onClose();
        // So the active channel is visible/highlighted once the sidebar is expanded again.
        setChannelsExpanded(true);
        sessionStorage.setItem('sb-channels-expanded', 'true');
      } else {
        onAddChannel();
      }
      return;
    }
    setChannelsExpanded((p) => {
      const next = !p;
      sessionStorage.setItem('sb-channels-expanded', String(next));
      return next;
    });
  }, [isAuthenticated, collapsed, channels, navigate, onClose, onAddChannel]);
  const toggleChannelExpand = useCallback((guid: string) => {
    setExpandedChannels((prev) => {
      if (prev.has(guid)) {
        return new Set();
      } else {
        return new Set([guid]);
      }
    });
  }, []);
  const toggleHistory = useCallback(() => {
    if (!isAuthenticated) {
      setSignInPrompt("Sign in to view chat history");
      return;
    }
    if (collapsed) {
      setHistoryModalOpen(true);
      return;
    }
    setHistoryExpanded((p) => !p);
  }, [isAuthenticated, collapsed]);

  // These highlights belong to a route, so leaving that route clears them —
  // whatever caused the navigation. Clearing only in this file's own click
  // handlers missed anything outside it (e.g. the Digital Team strip, which
  // navigates to Digital Workforce and left "New chat" highlighted).
  useEffect(() => {
    const onChat = location.pathname === ROUTES.CHAT || location.pathname.startsWith(`${ROUTES.CHAT}/`);
    setChatActiveItem((prev) => {
      if (prev === 'add-amp' && location.pathname !== ROUTES.AMPS_CREATE) return null;
      if (prev === 'new-chat' && !onChat) return null;
      return prev;
    });
  }, [location.pathname]);

  const handleNavClick = useCallback(
    (item: NavItem) => {
      setActiveId(item.id);
      setChatActiveItem(null);
      if (item.route) navigate(item.route);
      onClose();
    },
    [navigate, onClose],
  );

  const handleLogout = useCallback(async () => {
    await logout();
    navigate(ROUTES.LOGIN);
  }, [logout, navigate]);

  const handleDeleteChannel = useCallback(
    async (channelGuid: string) => {
      const success = await deleteChannel(channelGuid);
      if (success) {
        setDeleteConfirmChannel(null);
        setChannelMenuOpen(null);
        // If we're on the deleted channel, navigate away
        if (location.pathname.includes(channelGuid)) {
          navigate(ROUTES.CHAT);
        }
      }
    },
    [deleteChannel, location.pathname, navigate],
  );

  const closeUploadModal = useCallback(() => {
    setUploadChannelGuid(null);
    setChannelSelectedFile(null);
    setChannelUploadError(null);
    setChannelIsDragOver(false);
    setChannelModalUploads([]);
  }, []);

  const handleChannelFileUpload = useCallback(async () => {
    if (!uploadChannelGuid || !channelSelectedFile) return;
    const channel = channels.find(c => c.guid === uploadChannelGuid);
    if (!channel) return;
    const authToken = localStorage.getItem('authToken') || '';
    const AUTH_URL = process.env.REACT_APP_Auth_Url;
    setChannelUploading(true);
    setChannelUploadError(null);
    try {
      const formData = new FormData();
      formData.append('transactionId', crypto.randomUUID());
      formData.append('channelId', channel.id);
      formData.append('formFile', channelSelectedFile);
      const res = await fetch(`${AUTH_URL}/api/momentumamp/channel-file`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: formData,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const data = await res.json().catch(() => null);
      if (data?.fileUrl && data?.fileName) {
        const doc = { fileUrl: data.fileUrl, fileName: data.fileName };
        setChannelUploadedDocs(prev => [...prev, doc]);
        setChannelModalUploads(prev => [...prev, doc]);
      }
      setChannelSelectedFile(null);
    } catch (err: any) {
      setChannelUploadError(err.message || 'Upload failed');
    } finally {
      setChannelUploading(false);
    }
  }, [uploadChannelGuid, channelSelectedFile, channels, closeUploadModal]);

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? "sidebar-overlay--visible" : ""}`} onClick={onClose} />

      <motion.aside
        className={`sidebar-left ${isOpen ? "sidebar-left--open" : ""} ${collapsed ? "sidebar-left--collapsed" : ""}`}
        initial={false}
        animate={{ width: collapsed ? 72 : expandedWidth }}
        transition={{ type: "tween", ease: "easeOut", duration: 0.12 }}
      >
        {!isRateOnly && (
        <div className="sidebar-left__header">
          <div
            className="sidebar-left__brand-row"
            style={{ justifyContent: collapsed ? "center" : "flex-start" }}
            onClick={() => { navigate(ROUTES.CHAT); onClose(); }}
          >
            {collapsed ? (
              <img
                src={logoIcon}
                alt="InsuranceAgency.AI"
                className="sidebar-left__logo-collapsed-icon"
              />
            ) : (
              <img
                src={theme === 'light' ? logoLight : logo}
                alt="InsuranceAgency.AI"
                className="sidebar-left__logo-full"
              />
            )}
          </div>
          <button className={`sidebar-left__collapse-btn ${collapsed ? "sidebar-left__collapse-btn--rotated" : ""}`} onClick={toggleCollapse}>
            <FiChevronLeft size={16} />
          </button>
        </div>
        )}

        {!isRateOnly && (collapsed ? (
          <>
            <div
              className="nav-item"
              onClick={() => setSearchOpen(true)}
              title="Search"
              role="button"
              tabIndex={0}
              onKeyDown={activateOnKey(() => setSearchOpen(true))}
            >
              <span className="nav-item__icon">
                <img src={searchImg} alt="" className="sidebar-icon sidebar-icon--search sidebar-left__icon-search-collapsed" />
              </span>
            </div>
            {!isRateOnly && (
              <div
                className="nav-item"
                onClick={handleNewChat}
                title="New Chat"
                aria-label="New Chat"
                role="button"
                tabIndex={0}
                onKeyDown={activateOnKey(handleNewChat)}
              >
                <span className="nav-item__icon">
                  <FiEdit3 size={16} />
                </span>
              </div>
            )}
            {!isRateOnly && (
              <div
                className="nav-item"
                onClick={toggleHistory}
                title="Chat History"
                aria-label="Chat History"
                role="button"
                tabIndex={0}
                onKeyDown={activateOnKey(toggleHistory)}
              >
                <span className="nav-item__icon">
                  <FiClock size={16} />
                </span>
              </div>
            )}
            <div
              className="nav-item"
              onClick={() => { setQuickActionsViaKeyboard(false); setQuickActionsOpen(true); }}
              title={`Quick Actions (${QUICK_ACTIONS_SHORTCUT_LABEL})`}
              aria-label={`Quick Actions (${QUICK_ACTIONS_SHORTCUT_LABEL})`}
              role="button"
              tabIndex={0}
              onKeyDown={activateOnKey(() => { setQuickActionsViaKeyboard(false); setQuickActionsOpen(true); })}
            >
              <span className="nav-item__icon">
                <FiCommand size={16} />
              </span>
            </div>
          </>
        ) : (
          <div className="sidebar-left__search">
            <div
              className="sidebar-left__search-wrapper sidebar-left__search-wrapper--clickable"
              onClick={() => setSearchOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={activateOnKey(() => setSearchOpen(true))}
            >
              <span className="sidebar-left__search-icon">
                <img src={searchImg} alt="" className="sidebar-icon sidebar-icon--search sidebar-left__icon-search" />
              </span>
              <span className="sidebar-left__search-placeholder">Search...</span>
            </div>
          </div>
        ))}

        {!isRateOnly && !collapsed && (
          <div className="sidebar-left__quick-actions-row">
            {/* Three equal tiles — icon over a short label — so no one action
                reads as bigger than the others. Tooltips keep the full names
                (and Quick Actions' shortcut). */}
            <button
              type="button"
              className={`sidebar-left__qa-tile ${chatActiveItem === 'new-chat' ? 'sidebar-left__qa-tile--active' : ''}`}
              onClick={handleNewChat}
              title="New Chat"
            >
              <FiEdit3 size={16} aria-hidden="true" />
              <span className="sidebar-left__qa-label">New chat</span>
            </button>
            <button
              type="button"
              className={`sidebar-left__qa-tile ${historyExpanded ? 'sidebar-left__qa-tile--active' : ''}`}
              onClick={toggleHistory}
              title="Chat History"
              aria-label="Chat History"
              aria-expanded={historyExpanded}
            >
              <FiClock size={16} aria-hidden="true" />
              <span className="sidebar-left__qa-label">History</span>
            </button>
            <button
              type="button"
              className="sidebar-left__qa-tile"
              onClick={() => { setQuickActionsViaKeyboard(false); setQuickActionsOpen(true); }}
              title={`Quick Actions (${QUICK_ACTIONS_SHORTCUT_LABEL})`}
              aria-label={`Quick Actions (${QUICK_ACTIONS_SHORTCUT_LABEL})`}
            >
              <FiCommand size={16} aria-hidden="true" />
              <span className="sidebar-left__qa-label">Actions</span>
            </button>
          </div>
        )}

        {/* Chat History is a quick utility off the icon above, not a full nav
            section — working with your Digital Team is the main job of this
            sidebar now, so history doesn't get equal visual weight. */}
        {!isRateOnly && (
          <SafeAnimatePresence>
            {historyExpanded && !collapsed && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="sidebar-left__collapsible-panel sidebar-left__history-flyout"
              >
                <div className="chat-history">
                  {historyLoading ? (
                    <div className="chat-history__empty">Loading...</div>
                  ) : threads.length === 0 ? (
                    <div className="chat-history__empty">No conversations yet</div>
                  ) : (
                    threads.slice(0, 3).map((thread, index) => (
                      <motion.div
                        key={thread.id}
                        className={`chat-history__item has-archive-chat ${location.pathname === `/chat/${thread.id}` ? "chat-history__item--active" : ""}`}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setChatActiveItem(null);
                          setSelectedInsuredId(null);
                          setSelectedPolicyId(null);
                          openChatThread(thread.id);
                          onClose();
                        }}
                      >
                        <FiMessageSquare size={13} className="chat-history__icon" />
                        <span className="chat-history__title">{thread.title}</span>
                        <span className="chat-history__time">{formatRelativeTime(thread.updatedAt)}</span>
                        <ArchiveChatButton conversationId={thread.id} />
                      </motion.div>
                    ))
                  )}
                </div>

                {threads.length > 3 && (
                  <motion.div
                    className="chat-history__show-more"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setHistoryModalOpen(true);
                    }}
                  >
                    <FiClock size={12} />
                    <span>Show More</span>
                  </motion.div>
                )}
              </motion.div>
            )}
          </SafeAnimatePresence>
        )}

        <nav className={`sidebar-left__nav ${isRateOnly ? "sidebar-left__nav--no-header" : ""}`} aria-label="Primary">
          {!isRateOnly && <div className="glass-divider" />}
          {isAuthenticated && !isRateOnly && <SidebarDigitalTeam collapsed={collapsed} />}
          {isAuthenticated && !isRateOnly && <div className="glass-divider" />}

          {/* Channels Section - Channel-First Approach */}
          {isRateOnly ? (
            <div className="nav-section">
              {CENTERS.flatMap(center => center.workspaces)
                .filter(w => isWorkspaceEnabled(w))
                .map(w => {
                  const wActive = isWorkspaceActive(w, location.pathname);
                  return (
                    <div
                      key={w.path}
                      className={`nav-item ${wActive ? "nav-item--active" : ""}`}
                      onClick={() => { navigate(w.path); onClose(); }}
                      title={w.name}
                      role="button"
                      tabIndex={0}
                      aria-current={wActive ? "page" : undefined}
                      onKeyDown={activateOnKey(() => { navigate(w.path); onClose(); })}
                    >
                      <span className="nav-item__icon sidebar-left__nav-item-icon-flex">{w.icon}</span>
                      <span className="nav-item__label">{w.name}</span>
                    </div>
                  );
                })}
            </div>
          ) : (
          <div className="nav-section">
            {/* Pinned shortcuts — icon only, above the Workspaces tree. Pin or
                unpin from an item's "⋯" menu; right-click a pinned icon for
                the same menu. */}
            {isAuthenticated && pinnedWorkspaces.length > 0 && (
              <div
                className={`sidebar-left__pinned-bar ${collapsed ? "sidebar-left__pinned-bar--collapsed" : ""} ${draggingPin && pinDropIndex === pinnedPaths.length ? "sidebar-left__pinned-bar--drop-end" : ""}`}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) setPinDropIndex(null);
                }}
                role="group"
                aria-label="Pinned"
              >
                {pinnedWorkspaces.map(w => {
                  const wActive = isWorkspaceActive(w, location.pathname);
                  // Index into pinnedPaths (pinnedWorkspaces can skip disabled ones).
                  const pathIndex = pinnedPaths.indexOf(w.path);
                  const showDropBefore = draggingPin !== null && draggingPin !== w.path && pinDropIndex === pathIndex;
                  return (
                    <button
                      key={w.path}
                      type="button"
                      draggable
                      className={`sidebar-left__pinned-icon ${wActive ? "sidebar-left__pinned-icon--active" : ""} ${draggingPin === w.path ? "sidebar-left__pinned-icon--dragging" : ""} ${showDropBefore ? "sidebar-left__pinned-icon--drop-before" : ""}`}
                      onClick={() => { setChatActiveItem(null); navigate(w.path); onClose(); }}
                      onContextMenu={(e) => { e.preventDefault(); openWorkspaceMenu(e, w.path, w.name); }}
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", w.path);
                        setDraggingPin(w.path);
                      }}
                      onDragOver={(e) => {
                        if (!draggingPin) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        // Drop before or after this icon depending on which half you're over.
                        const rect = e.currentTarget.getBoundingClientRect();
                        const after = collapsed
                          ? e.clientY > rect.top + rect.height / 2
                          : e.clientX > rect.left + rect.width / 2;
                        setPinDropIndex(pathIndex + (after ? 1 : 0));
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (draggingPin && pinDropIndex !== null) {
                          const from = pinnedPaths.indexOf(draggingPin);
                          movePinned(draggingPin, from < pinDropIndex ? pinDropIndex - 1 : pinDropIndex);
                        }
                        setDraggingPin(null);
                        setPinDropIndex(null);
                      }}
                      onDragEnd={() => { setDraggingPin(null); setPinDropIndex(null); }}
                      onKeyDown={(e) => {
                        if (!e.altKey) return;
                        const back = collapsed ? e.key === "ArrowUp" : e.key === "ArrowLeft";
                        const fwd = collapsed ? e.key === "ArrowDown" : e.key === "ArrowRight";
                        if (!back && !fwd) return;
                        e.preventDefault();
                        movePinned(w.path, pathIndex + (fwd ? 1 : -1));
                      }}
                      title={`${w.name} — drag to reorder`}
                      aria-label={w.name}
                      aria-keyshortcuts={collapsed ? "Alt+ArrowUp Alt+ArrowDown" : "Alt+ArrowLeft Alt+ArrowRight"}
                      aria-current={wActive ? "page" : undefined}
                    >
                      {w.icon}
                    </button>
                  );
                })}
              </div>
            )}
            <div
              className={`nav-item nav-item--expandable nav-item--no-margin nav-item--no-margin-with-icon sidebar-left__nav-item--flat sidebar-left__nav-item--group-toggle ${channelsExpanded ? "nav-item--expanded" : ""}`}
              onClick={toggleChannels}
              role="button"
              tabIndex={0}
              aria-expanded={channelsExpanded}
              aria-controls="sidebar-left-workspaces-panel"
              onKeyDown={activateOnKey(toggleChannels)}
            >
              {collapsed ? null : (
                <span className="sidebar-left__icon-wrap-shrink0">
                  {channelsExpanded ? <IconChevronUp /> : <IconChevronDown />}
                </span>
              )}
              <span className="nav-item__icon"><FiGrid size={14} /></span>
              <span className="nav-item__label">Workspaces</span>
            </div>

            <SafeAnimatePresence initial={false}>
              {channelsExpanded && !collapsed && isAuthenticated && (
                <motion.div
                  id="sidebar-left-workspaces-panel"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="sidebar-left__collapsible-panel"
                >
                  {/* ── Tools (pinned top) ── */}
                  <div className="sidebar-left__tools-divider">
                    {hiddenMenuItemsLoaded && (() => {
                      const activeStyle = { borderRadius: 'var(--radius-md)' } as React.CSSProperties;
                      const inactiveStyle = { background: 'none', borderRadius: 0 } as React.CSSProperties;
                      return (<>
                        {/* Command Center sits above the Centers — it's the whole
                            agency's work queue, not a lifecycle stage. */}
                        {PINNED_WORKSPACES
                          .filter(w => isWorkspaceEnabled(w) && !hiddenMenuItems.has(w.path))
                          .map(w => {
                            const wActive = isWorkspaceActive(w, location.pathname);
                            return (
                              <div
                                key={w.path}
                                className={`nav-item sidebar-left__pinned-item ${wActive ? "nav-item--active" : ""}`}
                                onClick={() => { setChatActiveItem(null); navigate(w.path); onClose(); }}
                                style={wActive ? activeStyle : inactiveStyle}
                                title={w.blurb}
                                role="button"
                                tabIndex={0}
                                aria-current={wActive ? "page" : undefined}
                                onKeyDown={activateOnKey(() => { setChatActiveItem(null); navigate(w.path); onClose(); })}
                              >
                                <span className="nav-item__icon sidebar-left__nav-item-icon-flex">{w.icon}</span>
                                <span className="nav-item__label">{w.name}</span>
                                <span
                                  className="sidebar-left__nav-item-menu-trigger"
                                  onClick={(e) => openWorkspaceMenu(e, w.path, w.name)}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(148,163,184,0.1)'; e.currentTarget.style.opacity = '1'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.opacity = '0.6'; }}
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`More options for ${w.name}`}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openWorkspaceMenu(e, w.path, w.name); }
                                  }}
                                >
                                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <circle cx="6" cy="2" r="1" fill="currentColor" />
                                    <circle cx="6" cy="6" r="1" fill="currentColor" />
                                    <circle cx="6" cy="10" r="1" fill="currentColor" />
                                  </svg>
                                </span>
                              </div>
                            );
                          })}

                        {/* Restoring a hidden workspace used to require scrolling past
                            the entire Centers tree to find this toggle — moved up next
                            to the pinned tools so "undo" is one click away, not buried
                            (2026-09-23 nav critique, P3). */}
                        {hiddenMenuItems.size > 0 && (
                          <div className="sidebar-left__hidden-tools-wrap">
                            <div
                              className="sidebar-left__hidden-tools-toggle"
                              onClick={() => setShowHiddenTools(p => !p)}
                              role="button"
                              tabIndex={0}
                              aria-expanded={showHiddenTools}
                              onKeyDown={activateOnKey(() => setShowHiddenTools(p => !p))}
                            >
                              <svg width="11" height="11" viewBox="0 0 12 12" fill="none" className="sidebar-left__hidden-tools-chevron" style={{ transform: showHiddenTools ? 'rotate(90deg)' : 'none' }}>
                                <path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              <span>{hiddenMenuItems.size} hidden</span>
                            </div>
                            {showHiddenTools && (
                              <div className="sidebar-left__hidden-tools-list">
                                {ALL_WORKSPACES.filter(t => isWorkspaceEnabled(t) && hiddenMenuItems.has(t.path)).map(tool => (
                                  <div
                                    key={tool.path}
                                    className="nav-item sidebar-left__hidden-tool-row"
                                    title={`Show "${tool.name}"`}
                                  >
                                    <span className="nav-item__label sidebar-left__hidden-tool-label">{tool.name}</span>
                                    <span
                                      title={menuItemSaving.has(tool.path) ? 'Saving…' : 'Show in sidebar'}
                                      className="sidebar-left__hidden-tool-action"
                                      style={{ cursor: menuItemSaving.has(tool.path) ? 'default' : 'pointer', pointerEvents: menuItemSaving.has(tool.path) ? 'none' : 'auto' }}
                                      onClick={(e) => { e.stopPropagation(); handleShowMenuItem(tool.path); }}
                                      onMouseEnter={(e) => { if (!menuItemSaving.has(tool.path)) { e.currentTarget.style.background = 'rgba(148,163,184,0.1)'; e.currentTarget.style.opacity = '1'; } }}
                                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.opacity = '0.7'; }}
                                    >
                                      {menuItemSaving.has(tool.path) ? (
                                        <motion.svg width="13" height="13" viewBox="0 0 24 24" animate={{ rotate: 360 }} transition={{ duration: 0.7, repeat: Infinity, ease: 'linear' }} className="sidebar-left__shrink0">
                                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" fill="none" strokeOpacity="0.25" />
                                          <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                                        </motion.svg>
                                      ) : (
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                          <circle cx="12" cy="12" r="3" />
                                        </svg>
                                      )}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {CENTERS.map(center => {
                          const workspaces = center.workspaces.filter(
                            w => isWorkspaceEnabled(w) && !hiddenMenuItems.has(w.path)
                          );
                          if (workspaces.length === 0) return null;
                          const isExpanded = expandedCenterId === center.id;
                          const homePath = centerPath(center.id);
                          return (
                            <div key={center.id}>
                              <div
                                className={`nav-item nav-item--expandable nav-item--no-margin nav-item--no-margin-with-icon sidebar-left__nav-item--flat ${isExpanded ? "nav-item--expanded" : ""}`}
                                onClick={() => {
                                  // Clicking a Center goes into it, and toggles the
                                  // nested list open/closed — the whole row is the
                                  // hit target, not just the tiny chevron icon.
                                  setChatActiveItem(null);
                                  navigate(homePath);
                                  toggleCenter(center.id);
                                  onClose();
                                }}
                                title={center.blurb}
                                role="button"
                                tabIndex={0}
                                aria-expanded={isExpanded}
                                aria-controls={`sidebar-left-center-${center.id}`}
                                aria-current={location.pathname === homePath ? "page" : undefined}
                                onKeyDown={activateOnKey(() => {
                                  setChatActiveItem(null);
                                  navigate(homePath);
                                  toggleCenter(center.id);
                                  onClose();
                                })}
                              >
                                {collapsed ? null : (
                                  <span
                                    className="sidebar-left__icon-wrap-shrink0"
                                    onClick={(e) => { e.stopPropagation(); toggleCenter(center.id); }}
                                    title={isExpanded ? 'Collapse' : 'Expand'}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={isExpanded ? `Collapse ${center.name}` : `Expand ${center.name}`}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleCenter(center.id); }
                                    }}
                                  >
                                    {isExpanded ? <IconChevronUp /> : <IconChevronDown />}
                                  </span>
                                )}
                                <span className="nav-item__icon">{center.icon}</span>
                                <span className="nav-item__label">{center.name}</span>
                              </div>

                              <SafeAnimatePresence initial={false}>
                                {isExpanded && !collapsed && (
                                  <motion.div
                                    id={`sidebar-left-center-${center.id}`}
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.15 }}
                                    className="sidebar-left__collapsible-panel sidebar-left__center-items"
                                    role="group"
                                    aria-label={`${center.name} workspaces`}
                                  >
                                    {workspaces.map(w => {
                                      const wActive = isWorkspaceActive(w, location.pathname);
                                      return (
                                        <div
                                          key={w.path}
                                          className={`nav-item ${wActive ? "nav-item--active" : ""}`}
                                          onClick={() => { setChatActiveItem(null); navigate(w.path); onClose(); }}
                                          style={wActive ? activeStyle : inactiveStyle}
                                          title={w.name}
                                          role="button"
                                          tabIndex={0}
                                          aria-current={wActive ? "page" : undefined}
                                          onKeyDown={activateOnKey(() => { setChatActiveItem(null); navigate(w.path); onClose(); })}
                                        >
                                          <span className="nav-item__icon sidebar-left__nav-item-icon-flex">{w.icon}</span>
                                          <span className="nav-item__label">{w.name}</span>
                                          <span
                                            className="sidebar-left__nav-item-menu-trigger"
                                            onClick={(e) => openWorkspaceMenu(e, w.path, w.name)}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(148,163,184,0.1)'; e.currentTarget.style.opacity = '1'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.opacity = '0.6'; }}
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`More options for ${w.name}`}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openWorkspaceMenu(e, w.path, w.name); }
                                            }}
                                          >
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                              <circle cx="6" cy="2" r="1" fill="currentColor" />
                                              <circle cx="6" cy="6" r="1" fill="currentColor" />
                                              <circle cx="6" cy="10" r="1" fill="currentColor" />
                                            </svg>
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </motion.div>
                                )}
                              </SafeAnimatePresence>
                            </div>
                          );
                        })}
                      </>);
                    })()}
                  </div>

                          {!isRateOnly && (channelsLoading ? (
                            <div className="sidebar-left__channel-skeleton-wrap">
                              {[0, 1, 2].map(i => (
                                <div key={i} className="sidebar-left__channel-skeleton-item" style={{
                                  animation: `chat-shimmer 1.6s ${i * 0.1}s infinite linear`,
                                  opacity: 1 - i * 0.15,
                                }} />
                              ))}
                            </div>
                          ) : channels.length === 0 ? null : (
                            <div className="chat-history sidebar-left__chat-history--flush">
                              {channels.filter(channel =>
                                localStorage.getItem('showWelcomeChannel') !== 'false' ||
                                channel.name?.toLowerCase() !== 'welcome'
                              ).map((channel, index) => {
                                const channelPath = `/channel/${channel.guid}`;
                                const isActive = location.pathname === channelPath || location.pathname.startsWith(`${channelPath}/`);
                                const isChannelExpanded = expandedChannels.has(channel.guid);
                                const channelAmps = channel.ampIds
                                  .map((ampId) => amps.find((a) => a.id === ampId) || premadeAmps.find((a) => a.id === ampId))
                                  .filter((amp): amp is Amp => amp !== undefined);

                                return (
                                  <div key={channel.guid} className="channel-card sidebar-left__pos-relative">
                                    <div
                                      className={`nav-item nav-item--expandable nav-item--no-margin ${isChannelExpanded ? "nav-item--expanded" : ""} ${isActive ? "chat-history__item--active" : ""}`}
                                      style={(isActive || isChannelExpanded) ? { borderRadius: 6 } : { background: "none", borderRadius: 0 }}
                                      title={channel.name}
                                      onClick={() => {
                                        setChatActiveItem(null);
                                        toggleChannelExpand(channel.guid);
                                      }}
                                    >
                                      <span
                                        className="nav-item__chevron sidebar-left__channel-chevron"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleChannelExpand(channel.guid);
                                        }}
                                      >
                                        {isChannelExpanded ? <IconChevronUp /> : <IconChevronDown />}
                                      </span>
                                      <span
                                        className="nav-item__label sidebar-left__channel-label"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setChatActiveItem(null);
                                          navigate(channelPath);
                                          onClose();
                                        }}
                                      >
                                        {channel.name}
                                      </span>
                                      {channel.mcpIds && channel.mcpIds.length > 0 && (
                                        <span
                                          title="View attached MCP servers"
                                          className="sidebar-left__channel-mcp-badge"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            if (mcpPopoverChannel === channel.guid) {
                                              setMcpPopoverChannel(null);
                                              setMcpPopoverPos(null);
                                            } else {
                                              setMcpPopoverPos({ top: rect.bottom + 4, left: rect.left });
                                              setMcpPopoverChannel(channel.guid);
                                            }
                                          }}
                                          onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
                                          onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.75"; }}
                                        >
                                          <FiZap size={11} />
                                        </span>
                                      )}
                                      <span
                                        className="sidebar-left__channel-amp-count"
                                      >
                                        {channel.ampIds.length === 0 ? 'All' : channel.ampIds.length}
                                      </span>
                                      <span
                                        className="sidebar-left__channel-menu-trigger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          setMenuPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                                          setChannelMenuOpen(channelMenuOpen === channel.guid ? null : channel.guid);
                                          setNavMenuOpen(null);
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.background = "rgba(148,163,184,0.1)";
                                          e.currentTarget.style.opacity = "1";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background = "transparent";
                                          e.currentTarget.style.opacity = "0.6";
                                        }}
                                      >
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                          <circle cx="6" cy="2" r="1" fill="currentColor" />
                                          <circle cx="6" cy="6" r="1" fill="currentColor" />
                                          <circle cx="6" cy="10" r="1" fill="currentColor" />
                                        </svg>
                                      </span>
                                    </div>

                                    <SafeAnimatePresence>
                                      {isChannelExpanded && (
                                        <motion.div
                                          initial={{ height: 0, opacity: 0 }}
                                          animate={{ height: "auto", opacity: 1 }}
                                          exit={{ height: 0, opacity: 0 }}
                                          transition={{ duration: 0.15 }}
                                          className="channel-subitems sidebar-left__channel-subitems"
                                        >
                                          {channel.ampIds.length === 0 ? (
                                            <div className="sidebar-left__channel-empty-amps">
                                              <FiZap size={11} />
                                              All skills available
                                            </div>
                                          ) : channelAmps.map((amp, ampIndex) => {
                                            const specialActivePath =
                                              amp.category === 'Momentum Voice' || amp.category === 'Voice' ? ROUTES.VOICE :
                                              amp.category === 'Quote Proposal' ? ROUTES.QUOTE_PROPOSAL :
                                              amp.category === 'Carrier Download' ? ROUTES.CARRIER_DOWNLOAD :
                                              amp.category === 'Trucking Market Appetite' ? ROUTES.TRUCKING_APPETITE :
                                              amp.category === 'Carrier Finder' ? ROUTES.CARRIER_FINDER :
                                              amp.category === 'Rate Quoting Skill' || amp.category === 'RateQuoteLink' ? ROUTES.RATE_QUOTING :
                                              null;
                                            const directAmpPath = `/amp/${amp.id}`;
                                            const isAmpActive = specialActivePath
                                              ? (location.pathname === specialActivePath || location.pathname.startsWith(`${specialActivePath}/`))
                                              : (location.pathname === channelPath && location.search.includes(`amp=${amp.id}`))
                                                || location.pathname === directAmpPath
                                                || location.pathname.startsWith(`${directAmpPath}/`);
                                            return (
                                              <motion.div
                                                key={amp.id}
                                                className={`chat-history__item ${isAmpActive ? "chat-history__item--active" : ""}`}
                                                title={amp.name}
                                                initial={{ opacity: 0, x: -8 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: ampIndex * 0.02 }}
                                                onClick={() => {
                                                  setChatActiveItem(null);
                                                  const cat = amp.category;
                                                  if (cat === 'Momentum Voice' || cat === 'Voice') navigate(ROUTES.VOICE);
                                                  else if (cat === 'Quote Proposal') navigate(ROUTES.QUOTE_PROPOSAL);
                                                  else if (cat === 'Carrier Download') navigate(ROUTES.CARRIER_DOWNLOAD);
                                                  else if (cat === 'Trucking Market Appetite') navigate(ROUTES.TRUCKING_APPETITE);
                                                  else if (cat === 'Carrier Finder') navigate(ROUTES.CARRIER_FINDER);
                                                  else if (cat === 'Rate Quoting Skill' || cat === 'RateQuoteLink') navigate(ROUTES.RATE_QUOTING);
                                                  else navigate(`/channel/${channel.guid}?amp=${amp.id}`);
                                                  onClose();
                                                }}
                                              >
                                                <span className="chat-history__title sidebar-left__fs-12">
                                                  {amp.name}
                                                </span>
                                              </motion.div>
                                            );
                                          })}
                                        </motion.div>
                                      )}
                                    </SafeAnimatePresence>
                                  </div>
                                );
                              })}
                            </div>
                          ))}

                </motion.div>
              )}
            </SafeAnimatePresence>
          </div>
          )}

          {!isAuthenticated && (
            <SafeAnimatePresence>
              {signInPrompt && (
                <div className="sidebar-left__signin-wrap">
                  <SignInPrompt message={signInPrompt} onClose={() => setSignInPrompt(null)} />
                </div>
              )}
            </SafeAnimatePresence>
          )}

          {isAuthenticated && !isRateOnly && (
            <>
              <div className="glass-divider" />


              <div className="glass-divider" />
              <div className="nav-section">
                <div
                  className={`nav-item nav-item--expandable sidebar-left__nav-item--flat ${applicationExpanded ? "nav-item--expanded" : ""}`}
                  onClick={toggleApplication}
                >
                  {collapsed ? null : (
                    <span className="sidebar-left__icon-wrap-shrink0">
                      {applicationExpanded ? <IconMinus /> : <IconPlus />}
                    </span>
                  )}
                  <span className="nav-item__icon"><FiGrid size={14} /></span>
                  <span className="nav-item__label">Apps</span>
                </div>

                <SafeAnimatePresence>
                  {applicationExpanded && !collapsed && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="sidebar-left__collapsible-panel"
                    >
                      <div
                        className={`nav-item sidebar-left__nav-item--flat ${location.pathname.startsWith(ROUTES.MOMENTUM_CLAW) ? "nav-item--active" : ""}`}
                        onClick={() => { setChatActiveItem(null); navigate(ROUTES.MOMENTUM_CLAW); onClawNavClick?.(); }}
                      >
                        <span className="nav-item__icon sidebar-left__fs-14">🦀</span>
                        <span className="nav-item__label">MomentumClaw</span>
                      </div>

                      <SafeAnimatePresence>
                        {isOnClawChat && clawSkills.length > 0 && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="sidebar-left__claw-skills-panel"
                          >
                            {clawSkills.map((skill, index) => (
                              <motion.div
                                key={skill.id}
                                className="chat-history__item sidebar-left__cursor-pointer"
                                initial={{ opacity: 0, x: -8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.03 }}
                                title={skill.name}
                                onClick={() => onEditClawSkill?.(skill.id, skill.name)}
                              >
                                <span className="chat-history__title sidebar-left__fs-12">{skill.name}</span>
                              </motion.div>
                            ))}
                          </motion.div>
                        )}
                      </SafeAnimatePresence>

                      <SafeAnimatePresence>
                        {isOnClawChat && (
                          <motion.div
                            className="nav-add-channel sidebar-left__add-claw-skill"
                            onClick={() => onAddClawSkill?.()}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            <span className="nav-add-channel__icon">
                              <IconPlus />
                            </span>
                            <span>Add New Skill</span>
                          </motion.div>
                        )}
                      </SafeAnimatePresence>
                    </motion.div>
                  )}
                </SafeAnimatePresence>
              </div>

              <div className="glass-divider" />

              <div className="glass-divider" />
              {isMarketplaceMenuEnabled() && (
              <div className="nav-section">
                <div
                  className={`nav-item nav-item--expandable sidebar-left__nav-item--group-toggle ${myToolsExpanded ? "nav-item--expanded" : ""}`}
                  onClick={toggleMyTools}
                  style={{ background: "none", borderRadius: 0 }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={myToolsExpanded}
                  onKeyDown={activateOnKey(toggleMyTools)}
                >
                  {collapsed ? null : (
                    <span className="sidebar-left__icon-wrap-shrink0">
                      {myToolsExpanded ? <IconChevronUp /> : <IconChevronDown />}
                    </span>
                  )}
                  <span className="nav-item__icon"><FiShoppingBag size={14} /></span>
                  <span className="nav-item__label">Marketplace</span>
                </div>

                <SafeAnimatePresence>
                  {myToolsExpanded && !collapsed && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="sidebar-left__collapsible-panel"
                    >
                      <div
                        className={`nav-item ${location.pathname === ROUTES.MY_TOOLS && !location.search ? "nav-item--active" : location.pathname === ROUTES.MY_TOOLS && location.search.includes('view=creations') ? "nav-item--active" : ""}`}
                        onClick={() => { setChatActiveItem(null); navigate(`${ROUTES.MY_TOOLS}?view=creations`); }}
                        style={{ background: "none", borderRadius: 0 }}
                      >
                        <span className="nav-item__icon" style={{ fontSize: 13 }}>📋</span>
                        <span className="nav-item__label">My Creations</span>
                      </div>
                      <div
                        className={`nav-item ${location.search.includes('view=installed') ? "nav-item--active" : ""}`}
                        onClick={() => { setChatActiveItem(null); navigate(`${ROUTES.MY_TOOLS}?view=installed`); }}
                        style={{ background: "none", borderRadius: 0 }}
                      >
                        <span className="nav-item__icon" style={{ fontSize: 13 }}>📦</span>
                        <span className="nav-item__label">Installed</span>
                      </div>
                      <div
                        className={`nav-item ${location.search.includes('view=marketplace') ? "nav-item--active" : ""}`}
                        onClick={() => { setChatActiveItem(null); navigate(`${ROUTES.MY_TOOLS}?view=marketplace`); }}
                        style={{ background: "none", borderRadius: 0 }}
                      >
                        <span className="nav-item__icon" style={{ fontSize: 13 }}>🛍️</span>
                        <span className="nav-item__label">Browse</span>
                      </div>
                      <div
                        className={`nav-item ${location.search.includes('view=create') ? "nav-item--active" : ""}`}
                        onClick={() => { setChatActiveItem(null); navigate(`${ROUTES.MY_TOOLS}?view=create`); }}
                        style={{ background: "none", borderRadius: 0 }}
                      >
                        <span className="nav-item__icon" style={{ fontSize: 13 }}>✨</span>
                        <span className="nav-item__label">Create New</span>
                      </div>
                      {isReviewQueueEnabled() && (
                        <div
                          className={`nav-item ${location.pathname === ROUTES.REVIEW_QUEUE ? "nav-item--active" : ""}`}
                          onClick={() => { setChatActiveItem(null); navigate(ROUTES.REVIEW_QUEUE); }}
                          style={{ background: "none", borderRadius: 0 }}
                        >
                          <span className="nav-item__icon" style={{ fontSize: 13 }}>📋</span>
                          <span className="nav-item__label">Review Queue</span>
                        </div>
                      )}
                    </motion.div>
                  )}
                </SafeAnimatePresence>
              </div>
              )}
            </>
          )}
        </nav>

        {!isRateOnly && (
        <div className="sidebar-left__footer">
          {isAuthenticated ? (
            <>
              <div className={`sidebar-left__user-section${location.pathname === ROUTES.PROFILE ? ' sidebar-left__user-section--active' : ''}`} onClick={() => navigate(ROUTES.PROFILE)}>
                <div className="sidebar-left__user-avatar">{user?.name?.charAt(0) || "U"}</div>
                <div className="sidebar-left__user-info">
                  <div className="sidebar-left__user-name">{user?.name}</div>
                  <div className="sidebar-left__user-role">{user?.role}</div>
                </div>
              </div>
              <div className="sidebar-left__footer-actions">
                <button className={`sidebar-left__footer-btn ${location.pathname === ROUTES.PROFILE ? 'sidebar-left__footer-btn--active' : ''}`} onClick={() => { setChatActiveItem(null); navigate(ROUTES.PROFILE); }}>
                  <FiUser size={14} /> Profile
                </button>
                <button className={`sidebar-left__footer-btn ${location.pathname === ROUTES.SETTINGS ? 'sidebar-left__footer-btn--active' : ''}`} title="Settings" onClick={() => { setChatActiveItem(null); navigate(ROUTES.SETTINGS); }}>
                  <FiSettings size={14} /> Settings
                </button>
                <button
                  className="sidebar-left__footer-btn"
                  title="Open your AMS in a new tab"
                  onClick={() => {
                    const domain = localStorage.getItem('agencyDomain');
                    if (domain) window.open(`${domain}/AMSINS`, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <FiFileText size={14} /> Go to AMS
                </button>
                <button
                  className="sidebar-left__footer-btn"
                  title="Get help"
                  onClick={() => window.open('https://help.insuranceagency.ai', '_blank', 'noopener,noreferrer')}
                >
                  <FiHelpCircle size={14} /> Help
                </button>
                <button className="sidebar-left__footer-btn sidebar-left__footer-btn--danger" title="Logout" onClick={handleLogout}>
                  <FiLogOut size={14} /> Logout
                </button>
              </div>
            </>
          ) : (
            <div className="sidebar-left__footer-actions">
              <button className="sidebar-left__footer-btn" onClick={() => navigate(ROUTES.LOGIN)}>
                <FiUser size={14} /> Sign In
              </button>
              <button className="sidebar-left__footer-btn" onClick={() => navigate(ROUTES.SIGNUP)}>
                <FiPlus size={14} /> Sign Up
              </button>
              <button className="sidebar-left__footer-btn">
                <FiHelpCircle size={14} /> Help
              </button>
            </div>
          )}
        </div>
        )}
      </motion.aside>

      <SearchModal
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        threads={threads}
        channels={channels}
        amps={amps}
        onSelectThread={(id) => {
          openChatThread(id);
          onClose();
          setSearchOpen(false);
        }}
        onSelectChannel={(guid) => {
          navigate(`/channel/${guid}`);
          onClose();
          setSearchOpen(false);
        }}
        onSelectAmp={(id) => {
          if (id === '__voice__') navigate(ROUTES.VOICE);
          else if (id === '__proposal__') navigate(ROUTES.QUOTE_PROPOSAL);
          else if (id === '__carrier__') navigate(ROUTES.CARRIER_DOWNLOAD);
          else {
            const amp = premadeAmps.find(a => String(a.id) === String(id));
            const cat = amp?.category;
            if (cat === 'Momentum Voice' || cat === 'Voice') navigate(ROUTES.VOICE);
            else if (cat === 'Quote Proposal') navigate(ROUTES.QUOTE_PROPOSAL);
            else if (cat === 'Carrier Download') navigate(ROUTES.CARRIER_DOWNLOAD);
            else if (cat === 'Trucking Market Appetite') navigate(ROUTES.TRUCKING_APPETITE);
            else if (cat === 'Carrier Finder') navigate(ROUTES.CARRIER_FINDER);
            else if (cat === 'Rate Quoting Skill' || cat === 'RateQuoteLink') navigate(ROUTES.RATE_QUOTE_LINK);
            else navigate(`/amp/${id}`);
          }
          onClose();
          setSearchOpen(false);
        }}
        onSelectInsured={(ins) => {
          if ((ins.typeName || '').toLowerCase() === 'policy') setSelectedPolicyId(ins.id);
          else setSelectedInsuredId(ins.id);
          onClose();
          setSearchOpen(false);
        }}
      />

      <QuickActionsPalette
        isOpen={quickActionsOpen}
        onClose={() => setQuickActionsOpen(false)}
        openedViaKeyboard={quickActionsViaKeyboard}
        hiddenMenuItems={hiddenMenuItems}
        onNewChat={handleNewChat}
        onShowHiddenWorkspaces={handleShowHiddenWorkspaces}
        onAfterNavigate={onClose}
      />

      <HistoryModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        threads={threads}
        onSelectThread={(id) => {
          setSelectedInsuredId(null);
          setSelectedPolicyId(null);
          openChatThread(id);
          onClose();
        }}
      />

      {/* Channel Delete Menu - Fixed Position */}
      <SafeAnimatePresence>
        {navMenuOpen && menuPosition && (
          <>
            <div className="sidebar-left__menu-overlay" onClick={() => setNavMenuOpen(null)} />
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: -8 }}
              transition={{ duration: 0.12 }}
              className="channel-ctx-menu sidebar-left__ctx-menu-position"
              style={menuPosition.left !== undefined
                ? { top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }
                : { top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="channel-ctx-menu__item"
                role="button"
                tabIndex={0}
                onClick={() => { togglePinned(navMenuOpen.path); setNavMenuOpen(null); }}
                onKeyDown={activateOnKey(() => { togglePinned(navMenuOpen.path); setNavMenuOpen(null); })}
              >
                <IconPin off={pinnedPaths.includes(navMenuOpen.path)} />
                <span>{pinnedPaths.includes(navMenuOpen.path) ? 'Unpin' : 'Pin to top'}</span>
              </div>
              <div
                className="channel-ctx-menu__item"
                onClick={() => {
                  if (defaultPagePath === navMenuOpen.path) {
                    handleRemoveDefault();
                  } else {
                    handleSetDefault(navMenuOpen.path, navMenuOpen.name);
                  }
                  setNavMenuOpen(null);
                }}
              >
                <FiBookmark size={13} style={defaultPagePath === navMenuOpen.path ? { fill: 'currentColor', color: '#A200FF' } : {}} />
                <span>{defaultPagePath === navMenuOpen.path ? 'Remove Default' : 'Set as Default'}</span>
              </div>
              <div
                className="channel-ctx-menu__item"
                style={menuItemSaving.has(navMenuOpen.path) ? { opacity: 0.7, pointerEvents: 'none' } : {}}
                onClick={async () => {
                  await handleHideMenuItem(navMenuOpen.path);
                  setNavMenuOpen(null);
                }}
              >
                {menuItemSaving.has(navMenuOpen.path) ? (
                  <motion.svg width="13" height="13" viewBox="0 0 24 24" animate={{ rotate: 360 }} transition={{ duration: 0.7, repeat: Infinity, ease: 'linear' }} className="sidebar-left__shrink0">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" fill="none" strokeOpacity="0.25" />
                    <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                  </motion.svg>
                ) : (
                  <FiX size={13} />
                )}
                <span>Hide from Sidebar</span>
              </div>
            </motion.div>
          </>
        )}
        {channelMenuOpen && menuPosition && (
          <>
            {/* Invisible overlay to close menu */}
            <div
              className="sidebar-left__menu-overlay"
              onClick={() => setChannelMenuOpen(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: -8 }}
              transition={{ duration: 0.12 }}
              className="channel-ctx-menu sidebar-left__ctx-menu-position"
              style={{
                top: `${menuPosition.top}px`,
                right: `${menuPosition.right}px`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {onEditChannel && (
                <div
                  className="channel-ctx-menu__item"
                  onClick={() => {
                    const ch = channels.find(c => c.guid === channelMenuOpen);
                    if (ch) onEditChannel(ch);
                    setChannelMenuOpen(null);
                  }}
                >
                  <FiEdit3 size={13} />
                  <span>Edit</span>
                </div>
              )}
              <div
                className="channel-ctx-menu__item"
                onClick={() => {
                  setUploadChannelGuid(channelMenuOpen);
                  setChannelMenuOpen(null);
                }}
              >
                <FiFileText size={13} />
                <span>Upload File</span>
              </div>
              <div
                className="channel-ctx-menu__item"
                onClick={() => {
                  const channelPath = `/channel/${channelMenuOpen}`;
                  if (defaultPagePath === channelPath) {
                    handleRemoveDefault();
                  } else {
                    const ch = channels.find(c => c.guid === channelMenuOpen);
                    handleSetDefault(channelPath, ch?.name ?? '');
                  }
                  setChannelMenuOpen(null);
                }}
              >
                <FiBookmark size={13} style={defaultPagePath === `/channel/${channelMenuOpen}` ? { fill: 'currentColor', color: '#A200FF' } : {}} />
                <span>{defaultPagePath === `/channel/${channelMenuOpen}` ? 'Remove Default' : 'Set as Default'}</span>
              </div>
              <div
                className="channel-ctx-menu__item channel-ctx-menu__item--delete"
                onClick={() => {
                  setDeleteConfirmChannel(channelMenuOpen);
                  setChannelMenuOpen(null);
                }}
              >
                <FiX size={13} />
                <span>Delete</span>
              </div>
            </motion.div>
          </>
        )}
      </SafeAnimatePresence>

      {/* MCP Servers Popover */}
      <SafeAnimatePresence>
        {mcpPopoverChannel && mcpPopoverPos && (() => {
          const ch = channels.find(c => c.guid === mcpPopoverChannel);
          if (!ch?.mcpIds?.length) return null;
          return (
            <>
              <div className="sidebar-left__menu-overlay" onClick={() => { setMcpPopoverChannel(null); setMcpPopoverPos(null); }} />
              <motion.div
                key="mcp-popover"
                initial={{ opacity: 0, scale: 0.92, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: -4 }}
                transition={{ duration: 0.12 }}
                className="sidebar-left__mcp-popover"
                style={{
                  position: "fixed",
                  top: mcpPopoverPos.top,
                  left: mcpPopoverPos.left,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sidebar-left__mcp-popover-title">MCP Servers</div>
                {ch.mcpIds.map((m, i) => (
                  <div key={i} className="sidebar-left__mcp-popover-row">
                    <FiZap size={10} style={{ color: "#A200FF", flexShrink: 0 }} />
                    <span>{m.name}</span>
                  </div>
                ))}
              </motion.div>
            </>
          );
        })()}
      </SafeAnimatePresence>

      {/* Delete Confirmation Dialog */}
      <SafeAnimatePresence>
        {deleteConfirmChannel && (
          <div
            className="voice-modal-backdrop sidebar-left__z-10000"
            onClick={() => { setDeleteConfirmChannel(null); setChannelMenuOpen(null); }}
          >
            <motion.div
              className="voice-modal sidebar-left__delete-modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="sidebar-left__delete-modal-title">
                Delete Workspace?
              </h3>
              <p className="sidebar-left__delete-modal-body">
                Are you sure you want to delete "
                <strong className="sidebar-left__delete-modal-name">
                  {channels.find((c) => c.guid === deleteConfirmChannel)?.name}
                </strong>
                "? This action cannot be undone.
              </p>
              <div className="sidebar-left__delete-modal-actions">
                <button
                  className="glass-btn"
                  onClick={() => { setDeleteConfirmChannel(null); setChannelMenuOpen(null); }}
                >
                  Cancel
                </button>
                <button
                  className="glass-btn sidebar-left__delete-modal-confirm"
                  onClick={() => handleDeleteChannel(deleteConfirmChannel)}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#dc2626'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#ef4444'; }}
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </SafeAnimatePresence>

      {/* ── Hidden file input (triggered by drop zone button) ── */}
      <input
        ref={channelFileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
        className="sidebar-left__hidden-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) { setChannelSelectedFile(file); setChannelUploadError(null); }
          e.target.value = '';
        }}
      />

      {/* ── Channel Upload Modal ── */}
      <SafeAnimatePresence>
        {uploadChannelGuid && (
          <div className="voice-modal-backdrop sidebar-left__z-10000" onClick={closeUploadModal}>
            <motion.div
              className="voice-modal sidebar-left__upload-modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="voice-modal__header sidebar-left__upload-modal-header">
                <div className="sidebar-left__flex-gap-10">
                  <div className="sidebar-left__upload-modal-icon">
                    <FiFileText size={15} />
                  </div>
                  <div>
                    <div className="voice-modal__title">Upload File</div>
                    <div className="sidebar-left__upload-modal-channel-name">
                      {channels.find(c => c.guid === uploadChannelGuid)?.name}
                    </div>
                  </div>
                </div>
                <button className="voice-modal__close" onClick={closeUploadModal}>
                  <FiX size={16} />
                </button>
              </div>

              {/* Previously uploaded in this session */}
              {channelModalUploads.length > 0 && (
                <div className="sidebar-left__uploaded-docs-list">
                  {channelModalUploads.map((doc, i) => (
                    <div key={i} className="sidebar-left__uploaded-doc-row">
                      <FiFileText size={14} style={{ color: '#34d399', flexShrink: 0 }} />
                      <span className="sidebar-left__uploaded-doc-name">{doc.fileName}</span>
                      <button
                        onClick={() => { setChannelViewDoc(doc); closeUploadModal(); }}
                        className="sidebar-left__uploaded-doc-view-btn"
                      >
                        View
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected file preview */}
              {channelSelectedFile && (
                <div className="sidebar-left__selected-file-row">
                  <FiFileText size={15} style={{ color: '#a78bfa', flexShrink: 0 }} />
                  <div className="sidebar-left__selected-file-info">
                    <div className="sidebar-left__selected-file-name">{channelSelectedFile.name}</div>
                    <div className="sidebar-left__selected-file-size">
                      {channelSelectedFile.size < 1024 * 1024
                        ? `${(channelSelectedFile.size / 1024).toFixed(1)} KB`
                        : `${(channelSelectedFile.size / (1024 * 1024)).toFixed(1)} MB`}
                    </div>
                  </div>
                  <button onClick={() => setChannelSelectedFile(null)} className="sidebar-left__remove-file-btn">
                    <FiX size={13} />
                  </button>
                </div>
              )}

              {/* Drop zone */}
              <div
                onClick={() => channelFileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setChannelIsDragOver(true); }}
                onDragLeave={() => setChannelIsDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setChannelIsDragOver(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) { setChannelSelectedFile(file); setChannelUploadError(null); }
                }}
                className="sidebar-left__dropzone"
                style={{
                  border: `2px dashed ${channelIsDragOver ? '#a78bfa' : 'var(--border-glass)'}`,
                  background: channelIsDragOver ? 'rgba(139,92,246,0.08)' : 'transparent',
                }}
              >
                <div className="sidebar-left__dropzone-plus">+</div>
                <div className="sidebar-left__dropzone-title">Click or drag a file here</div>
                <div className="sidebar-left__dropzone-hint">PDF, DOC, DOCX, PNG, JPG · max 10 MB</div>
              </div>

              {/* Error */}
              {channelUploadError && (
                <div className="sidebar-left__upload-error">{channelUploadError}</div>
              )}

              {/* Actions */}
              <div className="voice-modal__footer">
                <button className="glass-btn" onClick={closeUploadModal}>Close</button>
                <button
                  className="glass-btn sidebar-left__upload-confirm-btn"
                  onClick={handleChannelFileUpload}
                  disabled={!channelSelectedFile || channelUploading}
                  style={{
                    background: channelSelectedFile ? 'var(--gradient-purple)' : undefined,
                    color: channelSelectedFile ? 'white' : 'var(--text-primary)',
                    opacity: !channelSelectedFile || channelUploading ? 0.5 : 1,
                  }}
                >
                  {channelUploading
                    ? <><span className="sidebar-left__upload-spinner" /> Uploading…</>
                    : 'Upload'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </SafeAnimatePresence>

      {/* ── Channel Document Viewer Modal ── */}
      <SafeAnimatePresence>
        {channelViewDoc && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="sidebar-left__doc-viewer-backdrop"
            onClick={() => setChannelViewDoc(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              transition={{ duration: 0.2 }}
              className="sidebar-left__doc-viewer-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sidebar-left__doc-viewer-header">
                <div className="sidebar-left__flex-gap-10">
                  <div className="sidebar-left__upload-modal-icon">
                    <FiFileText size={15} />
                  </div>
                  <span className="sidebar-left__doc-viewer-title">{channelViewDoc.fileName}</span>
                </div>
                <button onClick={() => setChannelViewDoc(null)} className="sidebar-left__doc-viewer-close">
                  <FiX size={16} />
                </button>
              </div>
              <div className="sidebar-left__doc-viewer-body">
                <iframe
                  src={channelViewDoc.fileUrl}
                  title={channelViewDoc.fileName}
                  className="sidebar-left__doc-viewer-iframe"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </SafeAnimatePresence>

      {/* Default page toast */}
      {defaultToast && ReactDOM.createPortal(
        <motion.div
          initial={{ opacity: 0, y: -16, x: 16 }}
          animate={{ opacity: 1, y: 0, x: 0 }}
          exit={{ opacity: 0, y: -16, x: 16 }}
          className="sidebar-left__default-toast"
          style={{
            background: defaultToast.type === 'success' ? 'rgba(22,163,74,0.15)' : 'rgba(239,68,68,0.15)',
            border: `1px solid ${defaultToast.type === 'success' ? 'rgba(74,222,128,0.3)' : 'rgba(252,165,165,0.3)'}`,
            color: defaultToast.type === 'success' ? '#4ade80' : '#fca5a5',
          }}
        >
          {defaultToast.type === 'success' ? <FiCheck size={14} /> : <FiAlertCircle size={14} />}
          {defaultToast.message}
        </motion.div>,
        document.body
      )}
    </>
  );
};

export default SidebarLeft;
